/**
 * EntityVendorsSection - Manage vendor assignments for an entity
 *
 * Allows admins to directly assign or remove users as vendors
 * without sending an invite email (useful for local testing and
 * bulk onboarding).
 *
 * @atomic @idempotent
 */
import { memo, useState, useEffect, useCallback } from 'react'
import { vendorAPI } from '../../../services/api/vendor'
import { adminAPI } from '../../../services/api'
import { useAsyncOperation } from '../../../hooks/useAsyncOperation'
import { toast } from '../../../services/toastService'
import { createLogger } from '../../../utils/logger'
import { XMarkIcon } from '@heroicons/react/24/outline'

const logger = createLogger('EntityVendorsSection')

const EntityVendorsSection = memo(function EntityVendorsSection({ entityId }) {
  const { execute, loading } = useAsyncOperation()
  const [vendors, setVendors] = useState([])
  const [search, setSearch] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [searchLoading, setSearchLoading] = useState(false)

  const loadVendors = useCallback(async () => {
    await execute(() => vendorAPI.getEntityVendors(entityId), {
      onSuccess: (r) => setVendors(r.data?.vendors ?? []),
      onError: () => logger.warn('Failed to load vendors'),
    })
  }, [entityId, execute])

  useEffect(() => { loadVendors() }, [loadVendors])

  const handleSearch = useCallback(async (e) => {
    e.preventDefault()
    if (!search.trim()) return
    setSearchLoading(true)
    setSelectedUser(null)
    try {
      const r = await adminAPI.getUsers({ search: search.trim(), limit: 5 })
      setSearchResults(r.data?.data ?? [])
    } catch {
      toast.error('User search failed')
    } finally {
      setSearchLoading(false)
    }
  }, [search])

  const handleAssign = useCallback(async (user) => {
    await execute(() => vendorAPI.assignVendor(entityId, user.id), {
      onSuccess: () => {
        toast.success(`${user.email} assigned as vendor`)
        setSearch('')
        setSearchResults([])
        setSelectedUser(null)
        loadVendors()
      },
      onError: (err) => toast.error(err?.message || 'Failed to assign vendor'),
    })
  }, [entityId, execute, loadVendors])

  const handleRemove = useCallback(async (vendor) => {
    await execute(() => vendorAPI.removeVendor(entityId, vendor.userId), {
      onSuccess: () => {
        toast.success(`${vendor.email} removed as vendor`)
        loadVendors()
      },
      onError: (err) => toast.error(err?.message || 'Failed to remove vendor'),
    })
  }, [entityId, execute, loadVendors])

  const activeVendors = vendors.filter(v => v.status === 'active')
  const suspendedVendors = vendors.filter(v => v.status === 'suspended')

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
      <h3 className="text-base font-semibold text-gray-800 mb-4">Vendor Management</h3>

      {/* Assign form */}
      <div className="space-y-3 mb-6">
        <p className="text-xs text-gray-500">
          Assign a registered user as a vendor for this entity. No email is sent — useful for local testing.
        </p>
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search user by email or name…"
            className="flex-1 text-sm border border-gray-200 rounded px-3 py-2"
          />
          <button
            type="submit"
            disabled={searchLoading || !search.trim()}
            className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            Search
          </button>
        </form>

        {/* Search results */}
        {searchResults.length > 0 && (
          <div className="border border-gray-200 rounded divide-y divide-gray-100">
            {searchResults.map(user => (
              <div key={user.id} className="flex items-center justify-between px-3 py-2">
                <div>
                  <span className="text-sm font-medium text-gray-700">{user.email}</span>
                  {(user.firstName || user.lastName) && (
                    <span className="text-xs text-gray-400 ml-2">{[user.firstName, user.lastName].filter(Boolean).join(' ')}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleAssign(user)}
                  disabled={loading}
                  className="px-3 py-1 text-xs bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
                >
                  Assign
                </button>
              </div>
            ))}
          </div>
        )}
        {searchResults.length === 0 && search && !searchLoading && selectedUser === null && (
          <p className="text-xs text-gray-400">No results. Try a different search.</p>
        )}
      </div>

      {/* Active vendors */}
      <div className="space-y-2">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Active Vendors ({activeVendors.length})
        </h4>
        {activeVendors.length === 0 && (
          <p className="text-xs text-gray-400">No active vendors assigned.</p>
        )}
        {activeVendors.map(vendor => (
          <div key={vendor.id} className="flex items-center justify-between bg-green-50 border border-green-100 rounded px-3 py-2">
            <div>
              <span className="text-sm font-medium text-gray-700">{vendor.email}</span>
              {vendor.displayName && (
                <span className="text-xs text-gray-400 ml-2">{vendor.displayName}</span>
              )}
              <span className="ml-2 inline-block px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">active</span>
              {vendor.grantedBy
                ? <span className="ml-1 inline-block px-1.5 py-0.5 text-xs bg-blue-50 text-blue-600 rounded">Admin-approved</span>
                : <span className="ml-1 inline-block px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">Self-claimed</span>
              }
            </div>
            <button
              type="button"
              onClick={() => handleRemove(vendor)}
              disabled={loading}
              className="text-red-400 hover:text-red-600 disabled:opacity-50"
              title="Remove vendor access"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Suspended vendors */}
      {suspendedVendors.length > 0 && (
        <div className="space-y-2 mt-4">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Suspended ({suspendedVendors.length})
          </h4>
          {suspendedVendors.map(vendor => (
            <div key={vendor.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded px-3 py-2">
              <div>
                <span className="text-sm text-gray-500">{vendor.email}</span>
                <span className="ml-2 inline-block px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">suspended</span>
              </div>
              <button
                type="button"
                onClick={() => handleAssign({ id: vendor.userId, email: vendor.email })}
                disabled={loading}
                className="px-3 py-1 text-xs bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
              >
                Reinstate
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
})

export default EntityVendorsSection
